import { useEffect, useRef, type RefObject } from 'react'
import { useLayer } from './layers'

// Every date picker closes on a press outside it and its own trigger, and on
// Escape (h 440-447): the dashboard, Product activity, Batches, Timesheets and
// Wages all take their period from one of these. It is implemented once: each
// open picker registers the wrapper its popover and trigger share, and one
// capture-phase listener serves them all. A press closes every open picker
// whose wrapper it landed outside, and still reaches what it landed on; the
// trigger is inside its own wrapper, so it goes on toggling. An open picker is
// a layer (layers.tsx), so Escape closes it and not a modal it floats over.

type Entry = { wrap: RefObject<HTMLElement | null>; close: () => void }

const openPickers = new Set<Entry>()

function onDown(e: MouseEvent) {
  const t = e.target as Node
  for (const p of [...openPickers]) if (!p.wrap.current?.contains(t)) p.close()
}

// The number of pickers open now; for tests.
export function openPickerCount() {
  return openPickers.size
}

// The ref goes on the element that holds both the trigger and the popover.
export function usePickerDismiss<T extends HTMLElement = HTMLDivElement>(open: boolean, close: () => void) {
  const wrap = useRef<T>(null)
  const closeRef = useRef(close)
  closeRef.current = close
  useLayer(open, () => closeRef.current(), { el: wrap })
  useEffect(() => {
    if (!open) return
    const entry: Entry = { wrap, close: () => closeRef.current() }
    if (openPickers.size === 0) document.addEventListener('mousedown', onDown, true)
    openPickers.add(entry)
    return () => {
      openPickers.delete(entry)
      if (openPickers.size === 0) document.removeEventListener('mousedown', onDown, true)
    }
  }, [open])
  return wrap
}
